import axios from 'axios';
import {ChangeEvent, useMemo, useState} from 'react';
import styles from './LeadImportPage.module.scss';

type ImportPreview = { headers: string[]; rows: string[][]; headersDetected: boolean };
type ImportError = { row: number; message: string };
type ImportResult = { imported: number; skipped: number; errors: ImportError[] };

const crmFields = [
    {id: '', label: 'Do not import'}, {id: 'name', label: 'Name'}, {id: 'company', label: 'Company'},
    {id: 'email', label: 'Email'}, {id: 'phone', label: 'Phone'}, {id: 'sourceUrl', label: 'Source URL'},
    {id: 'note', label: 'Note'}, {id: 'status', label: 'Status'}, {id: 'nextFollowUp', label: 'Next follow-up'},
];

function suggestField(header: string, columnValues: string[]) {
    const value = header.toLowerCase().replace(/[^\p{L}\p{N}]/gu, '');
    if (value.includes('email') || value.includes('mail') || value.includes('почта')) return 'email';
    if (value.includes('phone') || value.includes('mobile') || value.includes('tel') || value.includes('телефон')) return 'phone';
    if (value.includes('company') || value.includes('business') || value.includes('organization') || value.includes('компания')) return 'company';
    if (value.includes('name') || value.includes('contact') || value.includes('имя') || value.includes('контакт')) return 'name';
    if (value.includes('website') || value.includes('url') || value.includes('site') || value.includes('сайт')) return 'sourceUrl';
    if (value.includes('note') || value.includes('comment') || value.includes('заметка') || value.includes('комментарий')) return 'note';
    if (value.includes('follow') || value.includes('nextdate') || value.includes('дата')) return 'nextFollowUp';
    if (value.includes('status') || value.includes('статус')) return 'status';

    const values = columnValues.map(item => item.trim()).filter(Boolean);
    if (values.length === 0) return '';
    if (values.every(item => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(item))) return 'email';
    if (values.every(item => /^\+?[\d().\-\s]{7,}$/.test(item))) return 'phone';
    if (values.every(item => /^https?:\/\//i.test(item))) return 'sourceUrl';
    if (values.every(item => /^(new|contacted|follow-up|won|lost)$/i.test(item))) return 'status';
    if (values.every(item => /^\d{4}-\d{1,2}-\d{1,2}$/.test(item) || /^\d{1,2}[./-]\d{1,2}[./-]\d{2,4}$/.test(item))) return 'nextFollowUp';
    return '';
}

function createSuggestedMapping(preview: ImportPreview): Record<string, string> {
    const usedFields = new Set<string>();
    return Object.fromEntries(preview.headers.map((header, columnIndex) => {
        const values = preview.rows.map(row => row[columnIndex] ?? '');
        const suggested = suggestField(header, values);
        const field = suggested && !usedFields.has(suggested) ? suggested : '';
        if (field) usedFields.add(field);
        return [header, field];
    }));
}

export function LeadImportPage() {
    const [file, setFile] = useState<File | null>(null);
    const [preview, setPreview] = useState<ImportPreview | null>(null);
    const [mapping, setMapping] = useState<Record<string, string>>({});
    const [message, setMessage] = useState('');
    const [isError, setIsError] = useState(false);
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState<ImportResult | null>(null);
    const selectedFields = useMemo(() => new Set(Object.values(mapping).filter(Boolean)), [mapping]);
    const showError = (text: string) => { setMessage(text); setIsError(true); };

    const selectFile = async (event: ChangeEvent<HTMLInputElement>) => {
        const selected = event.target.files?.[0] ?? null;
        setFile(selected); setPreview(null); setMapping({}); setResult(null); setMessage('');
        if (!selected) return;
        if (!/\.(csv|xlsx)$/i.test(selected.name)) return showError('Choose a CSV or XLSX file.');
        if (selected.size > 10 * 1024 * 1024) return showError('The file must be 10 MB or smaller.');
        setLoading(true);
        try {
            const form = new FormData(); form.append('file', selected);
            const response = await axios.post<ImportPreview>('/api/extension/leads/import/preview', form);
            setPreview(response.data);
            setMapping(createSuggestedMapping(response.data));
            setMessage(response.data.headersDetected
                ? 'Column headers detected. Match your columns, then start the import.'
                : 'No column headers detected. The first row will be imported as data. Match your columns, then start the import.');
            setIsError(false);
        } catch (error: any) {
            showError(error.response?.data?.title || error.response?.data || 'Could not read this file.');
        } finally { setLoading(false); }
    };

    const importFile = async () => {
        if (!file || !preview) return;
        const activeMapping = Object.fromEntries(Object.entries(mapping).filter(([, value]) => value));
        if (Object.keys(activeMapping).length === 0) return showError('Match at least one column to a CRM field.');
        setLoading(true); setMessage('Importing leads...'); setIsError(false);
        try {
            const form = new FormData(); form.append('file', file); form.append('mapping', JSON.stringify(activeMapping));
            const response = await axios.post<ImportResult>('/api/extension/leads/import', form, {timeout: 5 * 60 * 1000});
            setResult(response.data); setMessage(`Imported ${response.data.imported} lead${response.data.imported === 1 ? '' : 's'}.`);
        } catch (error: any) {
            showError(error.response?.data?.title || error.response?.data || 'The import could not be completed.');
        } finally { setLoading(false); }
    };

    return <main className={styles.host}><section className={styles.content}>
        <h1>Import contacts</h1>
        <input type="file" accept=".csv,.xlsx,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" onChange={selectFile}/>
        <p className={styles.hint}>CSV or XLSX, up to 10 MB and 10,000 data rows.</p>
        {preview && <>
            <h2>Match columns</h2><div className={styles.mapping}>{preview.headers.map(header => <label key={header}><span>{header}</span>
                <select value={mapping[header] ?? ''} onChange={event => setMapping({...mapping, [header]: event.target.value})}>
                    {crmFields.map(field => <option key={field.id} value={field.id} disabled={!!field.id && selectedFields.has(field.id) && mapping[header] !== field.id}>{field.label}</option>)}
                </select></label>)}</div>
            <h2>Preview</h2><div className={styles.preview}><table><thead><tr>{preview.headers.map(header => <th key={header}>{header}</th>)}</tr></thead>
                <tbody>{preview.rows.map((row, rowIndex) => <tr key={rowIndex}>{preview.headers.map((_, columnIndex) => <td key={columnIndex}>{row[columnIndex] ?? ''}</td>)}</tr>)}</tbody></table></div>
            <button type="button" onClick={importFile} disabled={loading}>Import leads</button>
        </>}
        {message && <p className={isError ? styles.error : styles.message}>{message}</p>}
        {result && <section className={styles.report}><strong>Import report</strong><p>Imported: {result.imported}. Skipped: {result.skipped}.</p>
            {result.errors.length > 0 && <ul>{result.errors.map(error => <li key={`${error.row}-${error.message}`}>Row {error.row}: {error.message}</li>)}</ul>}</section>}
    </section></main>;
}
